"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { AppleLogo, CircleNotch } from "@phosphor-icons/react/dist/ssr";
import { Button } from "@/components/ui/button";
import { APPLE_CLIENT_ID, APPLE_REDIRECT_URI } from "@/lib/constants";

const APPLE_SDK_ID = "appleid-auth-sdk";
const APPLE_SDK_SRC =
    "https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js";

interface AppleSignInResult {
    authorization?: {
        id_token?: string;
        code?: string;
    };
}

interface AppleAuthError {
    error?: string;
}

declare global {
    interface Window {
        AppleID?: {
            auth: {
                init: (config: {
                    clientId: string;
                    scope: string;
                    redirectURI: string;
                    usePopup?: boolean;
                }) => void;
                signIn: (
                    config?: Record<string, unknown>,
                ) => Promise<AppleSignInResult>;
            };
        };
    }
}

let appleSdkPromise: Promise<void> | null = null;

/** Loads Apple's JS SDK once and resolves when `window.AppleID` is ready. */
function loadAppleSdk(): Promise<void> {
    if (typeof window === "undefined") return Promise.resolve();
    if (window.AppleID) return Promise.resolve();
    if (appleSdkPromise) return appleSdkPromise;

    appleSdkPromise = new Promise<void>((resolve, reject) => {
        const script =
            (document.getElementById(APPLE_SDK_ID) as HTMLScriptElement | null) ??
            document.createElement("script");

        script.id = APPLE_SDK_ID;
        script.addEventListener("load", () => resolve());
        script.addEventListener("error", () => {
            appleSdkPromise = null;
            reject(new Error("Failed to load Apple SDK"));
        });

        if (!script.src) {
            script.src = APPLE_SDK_SRC;
            script.async = true;
            document.body.appendChild(script);
        }
    });

    return appleSdkPromise;
}

let appleInitialized = false;

/** Initializes `AppleID.auth` at most once (Apple guidance). */
function initApple(): void {
    if (appleInitialized || !window.AppleID) return;
    window.AppleID.auth.init({
        clientId: APPLE_CLIENT_ID,
        scope: "name email",
        redirectURI: APPLE_REDIRECT_URI || window.location.origin,
        usePopup: true,
    });
    appleInitialized = true;
}

interface AppleSignInButtonProps {
    onSuccess: (idToken: string) => void | Promise<void>;
    onError?: (error: unknown) => void;
    onCancel?: () => void;
    disabled?: boolean;
}

/**
 * "Sign in with Apple" via Apple's JS SDK popup flow. Returns the Apple identity
 * token to `onSuccess`; the caller exchanges it with the backend. Renders nothing
 * when `NEXT_PUBLIC_APPLE_CLIENT_ID` is not configured.
 */
export function AppleSignInButton({
    onSuccess,
    onError,
    onCancel,
    disabled,
}: AppleSignInButtonProps) {
    const t = useTranslations();
    const [loading, setLoading] = useState(false);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    // Preload the SDK and initialize once, so the first click opens immediately.
    useEffect(() => {
        if (!APPLE_CLIENT_ID) return;
        loadAppleSdk()
            .then(initApple)
            .catch(() => {
                /* surfaced on click */
            });
    }, []);

    const handleClick = useCallback(async () => {
        if (!APPLE_CLIENT_ID) return;
        setLoading(true);
        try {
            // Keep the click a real user gesture when the SDK is already loaded.
            if (!window.AppleID) {
                await loadAppleSdk();
            }
            if (!window.AppleID) {
                throw new Error("Apple SDK unavailable");
            }

            initApple();

            const result = await window.AppleID.auth.signIn();
            const idToken = result?.authorization?.id_token;
            if (!idToken) {
                throw new Error("Missing Apple identity token");
            }

            await onSuccess(idToken);
        } catch (error) {
            const code = (error as AppleAuthError)?.error;
            if (
                code === "popup_closed_by_user" ||
                code === "user_cancelled_authorize"
            ) {
                onCancel?.();
            } else {
                onError?.(error);
            }
        } finally {
            if (mountedRef.current) {
                setLoading(false);
            }
        }
    }, [onSuccess, onError, onCancel]);

    if (!APPLE_CLIENT_ID) return null;

    return (
        <Button
            type="button"
            onClick={handleClick}
            disabled={disabled || loading}
            aria-label={t("auth.signInWithApple")}
            className="w-full bg-black text-white hover:bg-zinc-800 dark:bg-white dark:text-black dark:hover:bg-zinc-200"
        >
            {loading ? (
                <CircleNotch className="size-5 animate-spin" />
            ) : (
                <AppleLogo weight="fill" className="size-5" />
            )}
            {t("auth.signInWithApple")}
        </Button>
    );
}
